import type { Axis, Vec3 } from './model.js';
import type { AssemblyInstance, AssemblyPanel, ProjectDocument, Transform } from './document.js';
import { flipPanel, nextPanelKey, panelSize } from './assembly.js';

const IDX: Record<Axis, 0 | 1 | 2> = { x: 0, y: 1, z: 2 };
const NEAR = 500; // 0.5 mm
const near = (a: number, b: number) => Math.abs(a - b) <= NEAR;
const step = (v: number) => Math.round(v / 100) * 100; // 0.1 mm grid for proportional positions

/** Which panel dimension lies on a local axis (0 = x, 1 = y, 2 = z). */
export const panelPropertyOnAxis = (p: AssemblyPanel, k: 0 | 1 | 2): 'length' | 'width' | 'thickness' => {
  const s = panelSize({ ...p, length: 1, width: 2, thickness: 3 });
  return s[k] === 1 ? 'length' : s[k] === 2 ? 'width' : 'thickness';
};

/** Extent of the panel along each axis including repeated copies. */
function panelExtent(p: AssemblyPanel): { min: Vec3; max: Vec3 } {
  const size = panelSize(p);
  const min: Vec3 = [...p.position];
  const max: Vec3 = [0, 1, 2].map((k) => p.position[k]! + size[k]!) as Vec3;
  if (p.repeat && p.repeat.count > 1) max[IDX[p.repeat.axis]] += (p.repeat.count - 1) * p.repeat.spacing;
  return { min, max };
}

/** Overall box of an assembly in its local space (µm). */
export function assemblyBox(inst: Pick<AssemblyInstance, 'panels'>): { min: Vec3; max: Vec3; size: Vec3 } {
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const p of inst.panels) {
    const e = panelExtent(p);
    for (const k of [0, 1, 2] as const) {
      min[k] = Math.min(min[k], e.min[k]);
      max[k] = Math.max(max[k], e.max[k]);
    }
  }
  if (!inst.panels.length) return { min: [0, 0, 0], max: [0, 0, 0], size: [0, 0, 0] };
  return { min, max, size: [0, 1, 2].map((k) => max[k]! - min[k]!) as Vec3 };
}

/**
 * Changes the overall size of an assembly along one axis while keeping how the panels relate, like
 * linked panels would: panels whose thickness lies on that axis keep their thickness (the ones at the
 * outer faces stay on those faces, inner ones such as dividers keep their relative position); the other
 * panels stretch between the faces they touch and keep their distance to the nearest face elsewhere
 * (a recessed shelf stays recessed, a front rail keeps its width). The minimum side stays in place.
 */
export function resizeAssembly(inst: AssemblyInstance, axis: Axis, newSize: number): AssemblyInstance | { error: string } {
  const k = IDX[axis];
  const box = assemblyBox(inst);
  const m = box.min[k];
  const M = box.max[k];
  const S = M - m;
  if (!Number.isInteger(newSize) || newSize <= 0) return { error: 'Mjera mora biti pozitivna.' };
  if (S <= 0 || newSize === S) return inst;
  const delta = newSize - S;
  const ratio = newSize / S;
  const panels = structuredClone(inst.panels);

  // 1) Panels that are rigid along the axis (their thickness lies on it).
  const anchors: { from: number; to: number }[] = [
    { from: m, to: m },
    { from: M, to: M + delta },
  ];
  for (const p of panels) {
    if (panelPropertyOnAxis(p, k) !== 'thickness') continue;
    const e = panelExtent(p);
    const t = e.max[k] - e.min[k];
    const min = near(e.min[k], m) ? m : near(e.max[k], M) ? M + delta - t : step(m + ((e.min[k] + e.max[k]) / 2 - m) * ratio - t / 2);
    if (p.repeat && p.repeat.axis === axis) p.repeat = { ...p.repeat, spacing: step(p.repeat.spacing * ratio) };
    anchors.push({ from: e.min[k], to: min }, { from: e.max[k], to: min + t });
    p.position[k] = p.position[k] + (min - e.min[k]);
  }

  // 2) Every other coordinate follows a face it sits on, or keeps its distance to the nearest face.
  const map = (x: number): number => {
    const hit = anchors.find((a) => near(a.from, x));
    if (hit) return hit.to + (x - hit.from);
    let best = anchors[0]!;
    for (const a of anchors) if (Math.abs(a.from - x) < Math.abs(best.from - x)) best = a;
    return best.to + (x - best.from);
  };
  for (const p of panels) {
    const prop = panelPropertyOnAxis(p, k);
    if (prop === 'thickness') continue;
    const min = map(p.position[k]);
    const max = map(p.position[k] + p[prop]);
    if (max - min <= 0) return { error: `${p.label}: mjera bi bila ${((max - min) / 1000).toFixed(1)} mm.` };
    if (p.repeat && p.repeat.axis === axis && p.repeat.count > 1) p.repeat = { ...p.repeat, spacing: step(p.repeat.spacing * ratio) };
    p.position[k] = min;
    p[prop] = max - min;
  }
  return { ...inst, panels };
}

// ---------- moving a panel to another assembly ----------

function rot(t: Transform) {
  const phi = (-t.rotationY * Math.PI) / 180;
  return { cos: Math.round(Math.cos(phi)), sin: Math.round(Math.sin(phi)) };
}

/** Local box → world box for an assembly transform (mirroring is not taken into account). */
function toWorld(t: Transform, min: Vec3, max: Vec3): { min: Vec3; max: Vec3 } {
  const { cos, sin } = rot(t);
  const xs: number[] = [];
  const zs: number[] = [];
  for (const x of [min[0], max[0]])
    for (const z of [min[2], max[2]]) {
      xs.push(t.position[0] + x * cos + z * sin);
      zs.push(t.position[2] - x * sin + z * cos);
    }
  return { min: [Math.min(...xs), t.position[1] + min[1], Math.min(...zs)], max: [Math.max(...xs), t.position[1] + max[1], Math.max(...zs)] };
}

function toLocalMin(t: Transform, wmin: Vec3, wmax: Vec3): Vec3 {
  const { cos, sin } = rot(t);
  const xs: number[] = [];
  const zs: number[] = [];
  for (const wx of [wmin[0], wmax[0]])
    for (const wz of [wmin[2], wmax[2]]) {
      const dx = wx - t.position[0];
      const dz = wz - t.position[2];
      xs.push(dx * cos - dz * sin);
      zs.push(dx * sin + dz * cos);
    }
  return [Math.round(Math.min(...xs)), wmin[1] - t.position[1], Math.round(Math.min(...zs))];
}

/** Shifts the assembly origin so no panel lies below/behind it; panels keep their place in the room. */
function normaliseOrigin(inst: AssemblyInstance) {
  const { min } = assemblyBox(inst);
  const shift = min.map((v) => (v < 0 ? -v : 0)) as Vec3;
  if (shift.every((v) => v === 0)) return;
  for (const p of inst.panels) p.position = [0, 1, 2].map((k) => p.position[k]! + shift[k]!) as Vec3;
  const { cos, sin } = rot(inst.transform);
  const t = inst.transform.position;
  inst.transform.position = [t[0] - (shift[0] * cos + shift[2] * sin), t[1] - shift[1], t[2] - (-shift[0] * sin + shift[2] * cos)];
}

/**
 * Moves a panel from one assembly to another, keeping where it is in the room. Its key is renamed if
 * taken, links and edge overrides follow it, joints with the old assembly are dropped, and an
 * assembly left without panels is removed. Returns the new key (or null if nothing was moved).
 */
export function movePanelToAssembly(doc: ProjectDocument, fromId: string, key: string, toId: string): string | null {
  const from = doc.assemblies.find((a) => a.id === fromId);
  const to = doc.assemblies.find((a) => a.id === toId);
  if (!from || !to || from === to || from.locked || to.locked) return null;
  const idx = from.panels.findIndex((p) => p.key === key);
  if (idx < 0) return null;
  const src = from.panels[idx]!;

  const size = panelSize(src);
  const world = toWorld(from.transform, src.position, [0, 1, 2].map((k) => src.position[k]! + size[k]!) as Vec3);
  // Quarter turns between the two frames: an odd count swaps the panel's X and Z.
  const turns = (((to.transform.rotationY - from.transform.rotationY) / 90) % 4 + 4) % 4;
  let moved: AssemblyPanel = turns % 2 ? flipPanel(src, 'y') : structuredClone(src);
  const newKey = to.panels.some((p) => p.key === key) ? nextPanelKey(to) : key;
  moved = { ...moved, key: newKey, joints: [], position: toLocalMin(to.transform, world.min, world.max) };
  to.panels.push(moved);
  from.panels.splice(idx, 1);
  for (const p of from.panels) p.joints = p.joints.filter((j) => j.key !== key);

  // Edge overrides are keyed by part local key (repeated copies: `${key}-n`).
  for (const [ok, ov] of Object.entries(from.edgeOverrides)) {
    const [partKey, edge] = ok.split(':') as [string, string];
    if (partKey === key || partKey.startsWith(`${key}-`)) {
      delete from.edgeOverrides[ok];
      to.edgeOverrides[`${newKey}${partKey.slice(key.length)}:${edge}`] = ov;
    }
  }
  for (const l of doc.links)
    for (const end of [l.a, l.b])
      if (end.kind === 'panel' && end.unitId === fromId && end.panelKey === key) {
        end.unitId = toId;
        end.panelKey = newKey;
      }
  if (!from.panels.length) doc.assemblies = doc.assemblies.filter((a) => a !== from);
  normaliseOrigin(to);
  return newKey;
}

// ---------- swapping the overlap at a corner joint ----------

export interface OverlapSwap {
  /** The panel that currently runs through (covers the other's end). */
  through: string;
  /** The panel that currently butts against it. */
  butting: string;
}

/**
 * Finds a corner (L) joint between two panels: the butting panel's end touches the through panel's
 * face, and the through panel's end is flush with the butting panel's outer face. Returns null for
 * T-joints, insets, repeated panels or panels that do not touch.
 */
export function findOverlap(a: AssemblyPanel, b: AssemblyPanel): OverlapSwap | null {
  for (const [q, p] of [
    [a, b],
    [b, a],
  ] as const) {
    if ((q.repeat && q.repeat.count > 1) || (p.repeat && p.repeat.count > 1)) return null;
    const Q = panelExtent(q);
    const P = panelExtent(p);
    const j = ([0, 1, 2] as const).find((k) => panelPropertyOnAxis(q, k) === 'thickness')!;
    const i = ([0, 1, 2] as const).find((k) => panelPropertyOnAxis(p, k) === 'thickness')!;
    if (i === j) continue;
    // P's end on Q's face (axis j)
    if (!near(P.max[j], Q.min[j]) && !near(P.min[j], Q.max[j])) continue;
    // Q covers P's thickness and is flush with P's outer face (axis i)
    if (!(Q.min[i] <= P.min[i] + NEAR && Q.max[i] >= P.max[i] - NEAR)) continue;
    if (!near(Q.min[i], P.min[i]) && !near(Q.max[i], P.max[i])) continue;
    // and they overlap on the third axis
    const k = ([0, 1, 2] as const).find((x) => x !== i && x !== j)!;
    if (Math.min(P.max[k], Q.max[k]) - Math.max(P.min[k], Q.min[k]) <= NEAR) continue;
    return { through: q.key, butting: p.key };
  }
  return null;
}

/** Swaps which panel runs through at the joint; the outer corner of the pair stays where it is. */
export function swapOverlap(inst: AssemblyInstance, keyA: string, keyB: string): AssemblyPanel[] | null {
  const a = inst.panels.find((p) => p.key === keyA);
  const b = inst.panels.find((p) => p.key === keyB);
  if (!a || !b) return null;
  const o = findOverlap(a, b);
  if (!o) return null;
  const panels = structuredClone(inst.panels);
  const q = panels.find((p) => p.key === o.through)!;
  const p = panels.find((x) => x.key === o.butting)!;
  const Q = panelExtent(q);
  const P = panelExtent(p);
  const j = ([0, 1, 2] as const).find((k) => panelPropertyOnAxis(q, k) === 'thickness')!;
  const i = ([0, 1, 2] as const).find((k) => panelPropertyOnAxis(p, k) === 'thickness')!;
  // The butting panel grows over the through panel's thickness...
  const pj = panelPropertyOnAxis(p, j);
  p[pj] += q.thickness;
  if (near(P.min[j], Q.max[j])) p.position[j] -= q.thickness;
  // ...and the through panel gives up the butting panel's thickness on the flush side.
  const qi = panelPropertyOnAxis(q, i);
  if (q[qi] - p.thickness <= 0) return null;
  q[qi] -= p.thickness;
  if (near(Q.min[i], P.min[i])) q.position[i] += p.thickness;
  return panels;
}

/**
 * Swaps the overlap of two panels in a document, and also at the matching joints of panels linked
 * to them (e.g. both sides linked equal sit on the bottom → the bottom goes under both), so the
 * links stay satisfied without moving the far ends. Returns false if there is no joint to swap.
 */
export function swapOverlapInDoc(doc: ProjectDocument, unitId: string, keyA: string, keyB: string): boolean {
  const inst = doc.assemblies.find((x) => x.id === unitId);
  const a = inst?.panels.find((p) => p.key === keyA);
  const b = inst?.panels.find((p) => p.key === keyB);
  const o = a && b ? findOverlap(a, b) : null;
  if (!inst || !o) return false;
  const byKey = (k: string) => inst.panels.find((p) => p.key === k)!;
  const pairs: [string, string][] = [[o.through, o.butting]];
  for (const l of doc.links) {
    for (const [mine, other] of [
      [l.a, l.b],
      [l.b, l.a],
    ] as const) {
      if (mine.kind !== 'panel' || other.kind !== 'panel' || mine.unitId !== unitId || other.unitId !== unitId) continue;
      const x = inst.panels.find((p) => p.key === other.panelKey);
      if (!x || pairs.some(([t, s]) => t === x.key || s === x.key)) continue;
      // The linked panel must have the same role at a joint with the same partner.
      const role = mine.panelKey === o.through ? 'through' : mine.panelKey === o.butting ? 'butting' : null;
      if (!role) continue;
      const partner = byKey(role === 'through' ? o.butting : o.through);
      const ox = findOverlap(x, partner);
      if (ox && ox[role] === x.key) pairs.push([ox.through, ox.butting]);
    }
  }
  let panels = inst.panels;
  for (const [t, s] of pairs) panels = swapOverlap({ ...inst, panels }, t, s) ?? panels;
  inst.panels = panels;
  return true;
}
